const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const toDisplayedRgb = ({ r, g, b }) => ({
  r: clamp(Math.round(r), 0, 255),
  g: clamp(Math.round(g), 0, 255),
  b: clamp(Math.round(b), 0, 255),
});

export const rgbToCss = (color) => {
  const { r, g, b } = toDisplayedRgb(color);
  return `rgb(${r}, ${g}, ${b})`;
};

export const hslToRgb = (h, s, l) => {
  const hue = ((h % 360) + 360) % 360;
  const saturation = clamp(s / 100, 0, 1);
  const lightness = clamp(l / 100, 0, 1);

  if (saturation === 0) {
    const grey = lightness * 255;
    return { r: grey, g: grey, b: grey };
  }

  const hueToRgb = (p, q, t) => {
    let temp = t;
    if (temp < 0) temp += 1;
    if (temp > 1) temp -= 1;
    if (temp < 1 / 6) return p + (q - p) * 6 * temp;
    if (temp < 1 / 2) return q;
    if (temp < 2 / 3) return p + (q - p) * (2 / 3 - temp) * 6;
    return p;
  };

  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;
  const hNormalized = hue / 360;

  return {
    r: clamp(Math.round(hueToRgb(p, q, hNormalized + 1 / 3) * 255), 0, 255),
    g: clamp(Math.round(hueToRgb(p, q, hNormalized) * 255), 0, 255),
    b: clamp(Math.round(hueToRgb(p, q, hNormalized - 1 / 3) * 255), 0, 255),
  };
};

export const rgbToHsl = ({ r, g, b }) => {
  const rNorm = clamp(r / 255, 0, 1);
  const gNorm = clamp(g / 255, 0, 1);
  const bNorm = clamp(b / 255, 0, 1);

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const lightness = (max + min) / 2;

  if (max === min) {
    return [0, 0, lightness];
  }

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue;
  switch (max) {
  case rNorm:
    hue = (gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0);
    break;
  case gNorm:
    hue = (bNorm - rNorm) / delta + 2;
    break;
  default:
    hue = (rNorm - gNorm) / delta + 4;
    break;
  }

  hue /= 6;

  return [hue * 360, saturation, lightness];
};

export const getRelativeLuminance = ({ r, g, b }) => {
  const transform = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  const rL = transform(r);
  const gL = transform(g);
  const bL = transform(b);

  return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
};

export const clamp01 = (value) => clamp(value, 0, 1);

const SUBTRACTIVE_PIGMENTS = {
  red: { r: 1, g: 0.05, b: 0.05 },
  yellow: { r: 1, g: 0.95, b: 0.05 },
  blue: { r: 0.05, g: 0.45, b: 1 },
  white: { r: 1, g: 1, b: 1 },
  black: { r: 0.05, g: 0.05, b: 0.05 },
};

const PIGMENT_STRENGTH = {
  white: 0.68,
  black: 1.28,
};

const mixSubtractivePigments = (components) => {
  const entries = Object.entries(components)
    .filter(([key, amount]) => amount > 0 && SUBTRACTIVE_PIGMENTS[key]);

  if (entries.length === 0) {
    return null;
  }

  const weighted = entries.map(([key, amount]) => ({
    key,
    weight: amount * (PIGMENT_STRENGTH[key] ?? 1),
  }));

  const totalWeight = weighted.reduce((sum, { weight }) => sum + weight, 0);

  if (totalWeight <= 0) {
    return null;
  }

  const reflectance = weighted.reduce((accumulator, { key, weight }) => {
    const pigment = SUBTRACTIVE_PIGMENTS[key];

    if (!pigment) {
      return accumulator;
    }

    const fraction = weight / totalWeight;

    return {
      r: accumulator.r * Math.pow(pigment.r, fraction),
      g: accumulator.g * Math.pow(pigment.g, fraction),
      b: accumulator.b * Math.pow(pigment.b, fraction),
    };
  }, { r: 1, g: 1, b: 1 });

  return {
    r: clamp(Math.round(reflectance.r * 255), 0, 255),
    g: clamp(Math.round(reflectance.g * 255), 0, 255),
    b: clamp(Math.round(reflectance.b * 255), 0, 255),
  };
};

export const mixComponentsToRgb = (components, palette, fallbackColor) => {
  const entries = Object.entries(components);
  const total = entries.reduce((sum, [, amount]) => sum + amount, 0);

  if (total <= 0) {
    return fallbackColor;
  }

  const redAmount = components.red ?? 0;
  const yellowAmount = components.yellow ?? 0;
  const blueAmount = components.blue ?? 0;
  const whiteAmount = components.white ?? 0;
  const blackAmount = components.black ?? 0;

  const primaries = [
    { key: 'red', amount: redAmount },
    { key: 'yellow', amount: yellowAmount },
    { key: 'blue', amount: blueAmount },
  ];

  const activePrimaries = primaries.filter(({ amount }) => amount > 0);

  if (activePrimaries.length === 1 && whiteAmount <= 0 && blackAmount <= 0) {
    const primaryKey = activePrimaries[0].key;
    return palette[primaryKey]?.rgb ?? fallbackColor;
  }

  const subtractiveMix = mixSubtractivePigments({
    red: redAmount,
    yellow: yellowAmount,
    blue: blueAmount,
    white: whiteAmount,
    black: blackAmount,
  });

  if (subtractiveMix) {
    return subtractiveMix;
  }

  const contributions = [];

  primaries.forEach(({ key, amount }) => {
    if (amount > 0) {
      const color = palette[key]?.rgb ?? fallbackColor;
      contributions.push({ amount, color });
    }
  });

  if (whiteAmount > 0) {
    contributions.push({ amount: whiteAmount, color: palette.white?.rgb ?? fallbackColor });
  }

  if (blackAmount > 0) {
    contributions.push({ amount: blackAmount, color: palette.black?.rgb ?? fallbackColor });
  }

  if (contributions.length === 0) {
    return fallbackColor;
  }

  const totalAmount = contributions.reduce((sum, contribution) => sum + contribution.amount, 0);

  const mixed = contributions.reduce((accumulator, { color, amount }) => ({
    r: accumulator.r + color.r * amount,
    g: accumulator.g + color.g * amount,
    b: accumulator.b + color.b * amount,
  }), { r: 0, g: 0, b: 0 });

  return {
    r: mixed.r / totalAmount,
    g: mixed.g / totalAmount,
    b: mixed.b / totalAmount,
  };
};

export const gradientBetween = (colorA, colorB) => `linear-gradient(90deg, ${rgbToCss(colorA)} 0%, ${rgbToCss(colorB)} 100%)`;
